import { useEffect, useState } from 'react';
import { LogOut, Award, Crown, Sparkles, ChevronRight, Download, Trash2, Shield, ShieldAlert, HelpCircle, CreditCard, Loader2, BarChart3, Link2 as LinkIcon, Wand2, RefreshCw, Camera, Share2, X, HardDrive, Cloud, Cpu, Smartphone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n';
import { api, fileUrl, pictureUrl } from '../lib/api';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import LanguageSwitcher from '../components/LanguageSwitcher';
import OperatorNotice from '../components/OperatorNotice';
import InviteCard from '../components/InviteCard';
import PushSettingsCard from '../components/PushSettingsCard';
import InstallPWAButton from '../components/InstallPWAButton';
import SubscriptionCard from '../components/SubscriptionCard';
import ThemeSwitcher from '../components/ThemeSwitcher';
import SocialLinks from '../components/SocialLinks';
import DisplayNameCard from '../components/DisplayNameCard';
import CommunityGuidelinesCard from '../components/CommunityGuidelinesCard';
import PremiumFromPrice from '../components/PremiumFromPrice';
import { toast } from 'sonner';

export default function Profil() {
    const { user, setUser, logout } = useAuth();
    const { t, locale } = useI18n();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const [ach, setAch] = useState(null);
    const [busy, setBusy] = useState(false);
    const [waitlist, setWaitlist] = useState(null);
    const [broadcasting, setBroadcasting] = useState(false);
    const [plants, setPlants] = useState([]);
    const [photoPicker, setPhotoPicker] = useState(false);
    const [profileBusy, setProfileBusy] = useState(false);
    const [storage, setStorage] = useState(null);
    const [aiPreference, setAiPreference] = useState(() => {
        try { return localStorage.getItem('growdaily_ai_preference') || 'managed'; } catch { return 'managed'; }
    });

    const changeAiPreference = (value) => {
        setAiPreference(value);
        try { localStorage.setItem('growdaily_ai_preference', value); } catch { /* preference is best effort */ }
        toast.success(locale === 'da'
            ? value === 'local' ? 'Lokal AI er valgt — tilslut en lokal model for at bruge den.' : 'GrowDaily AI er valgt.'
            : value === 'local' ? 'Local AI selected — connect a local model to use it.' : 'GrowDaily AI selected.');
    };

    useEffect(() => {
        api.get('/achievements').then((r) => setAch(r.data)).catch(() => setAch(null));
        api.get('/plants').then((r) => setPlants((r.data || []).filter((p) => p.image_path))).catch(() => setPlants([]));
        api.get('/storage/stats').then((r) => setStorage(r.data)).catch(() => setStorage(null));
    }, []);

    const chooseProfilePhoto = async (picture) => {
        setProfileBusy(true);
        try {
            const r = await api.patch('/auth/me', { picture });
            setUser(r.data);
            setPhotoPicker(false);
            toast.success(locale === 'da' ? 'Profilbilledet er opdateret' : 'Profile photo updated');
        } catch { toast.error(locale === 'da' ? 'Billedet kunne ikke gemmes' : 'Could not save photo'); }
        finally { setProfileBusy(false); }
    };

    const shareProfile = async () => {
        setProfileBusy(true);
        try {
            const canvas = document.createElement('canvas');
            canvas.width = 1200; canvas.height = 630;
            const ctx = canvas.getContext('2d');
            const grad = ctx.createLinearGradient(0, 0, 1200, 630);
            grad.addColorStop(0, '#173d2a'); grad.addColorStop(1, '#78945f');
            ctx.fillStyle = grad; ctx.fillRect(0, 0, 1200, 630);
            if (user.picture) {
                try {
                    const image = await new Promise((resolve, reject) => { const img = new Image(); img.crossOrigin = 'anonymous'; img.onload = () => resolve(img); img.onerror = reject; img.src = pictureUrl(user.picture); });
                    ctx.save(); ctx.beginPath(); ctx.arc(260, 315, 170, 0, Math.PI * 2); ctx.clip();
                    const scale = Math.max(340 / image.width, 340 / image.height);
                    ctx.drawImage(image, 260 - image.width * scale / 2, 315 - image.height * scale / 2, image.width * scale, image.height * scale); ctx.restore();
                    ctx.strokeStyle = '#f7f3e9'; ctx.lineWidth = 12; ctx.beginPath(); ctx.arc(260, 315, 176, 0, Math.PI * 2); ctx.stroke();
                } catch { /* initials below still make a valid card */ }
            }
            ctx.fillStyle = '#f7f3e9'; ctx.font = '700 34px Arial'; ctx.fillText('GrowDaily.fun', 500, 205);
            ctx.font = '700 68px Georgia'; ctx.fillText(user.name || 'Grower', 500, 300);
            ctx.font = '34px Arial'; ctx.fillStyle = '#e5eddc';
            ctx.fillText(locale === 'da' ? `${plants.length} planter i min digitale have` : `${plants.length} plants in my digital garden`, 500, 365);
            ctx.font = '28px Arial'; ctx.fillText(locale === 'da' ? 'Følg min have på GrowDaily 🌱' : 'Follow my garden on GrowDaily 🌱', 500, 430);
            const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', .9));
            const file = new File([blob], 'growdaily-profile.jpg', { type: 'image/jpeg' });
            const text = locale === 'da' ? `Se min have på GrowDaily.fun 🌱` : `See my garden on GrowDaily.fun 🌱`;
            if (navigator.share && navigator.canShare?.({ files: [file] })) await navigator.share({ title: user.name, text, files: [file], url: 'https://growdaily.fun' });
            else { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = file.name; a.click(); setTimeout(() => URL.revokeObjectURL(url), 2000); toast.success(locale === 'da' ? 'Delingsbilledet er downloadet' : 'Share image downloaded'); }
        } catch (error) { if (error?.name !== 'AbortError') toast.error(locale === 'da' ? 'Deling mislykkedes' : 'Sharing failed'); }
        finally { setProfileBusy(false); }
    };

    useEffect(() => {
        const lt = params.get('livetest');
        if (!lt) return;
        if (lt === 'ok') toast.success('✅ Live PayPal purchase completed — checkout works!');
        else if (lt === 'cancelled') toast('Live test cancelled');
        else toast.error('Live PayPal test did not complete');
        params.delete('livetest');
        setParams(params, { replace: true });
    }, [params, setParams]);

    useEffect(() => {
        const rn = params.get('rename');
        if (!rn) return;
        if (rn === 'paid') toast.success('Payment complete — you can rename now 🌱');
        else if (rn === 'cancelled') toast('Rename payment cancelled');
        else if (rn === 'failed') toast.error('Rename payment did not complete');
        params.delete('rename');
        setParams(params, { replace: true });
    }, [params, setParams]);

    const isFounder = !!user?.is_founder || !!user?.is_admin;
    const effectiveAiPreference = user?.premium ? 'managed' : aiPreference;

    useEffect(() => {
        if (!isFounder) return;
        api.get('/marketplace/waitlist/count').then((r) => setWaitlist(r.data)).catch(() => setWaitlist(null));
    }, [isFounder]);

    const broadcastWaitlist = async () => {
        if (!window.confirm('Send "Marketplace is open!" email + push to every waitlist subscriber now?')) return;
        setBroadcasting(true);
        try {
            const r = await api.post('/marketplace/broadcast');
            toast.success(`Sent to ${r.data?.recipients ?? 0} subscribers (${r.data?.emails_sent ?? 0} emails, ${r.data?.push_sent ?? 0} push)`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Broadcast failed');
        } finally { setBroadcasting(false); }
    };

    const exportData = async () => {
        setBusy(true);
        try {
            const r = await api.get('/auth/me/export');
            const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `growdaily-export-${new Date().toISOString().slice(0, 10)}.json`;
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1500);
            toast.success(t('profile.export_ok'));
        } catch {
            toast.error(t('profile.export_fail'));
        } finally { setBusy(false); }
    };

    const clearCacheAndReload = async () => {
        if (!window.confirm(t('profile.clear_cache_confirm'))) return;
        setBusy(true);
        try {
            // Preserve only the session + language so the user stays logged in
            const keep = {
                session_token: localStorage.getItem('session_token'),
                locale: localStorage.getItem('locale'),
                gd_theme: localStorage.getItem('gd_theme'),
            };
            try { localStorage.clear(); } catch { /* ignore */ }
            Object.entries(keep).forEach(([k, v]) => { if (v != null) localStorage.setItem(k, v); });
            try { sessionStorage.clear(); } catch { /* ignore */ }
            if ('caches' in window) {
                const names = await caches.keys();
                await Promise.all(names.map((n) => caches.delete(n)));
            }
            if ('serviceWorker' in navigator) {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map((r) => r.unregister()));
            }
            toast.success(t('profile.clear_cache_done'));
            setTimeout(() => { window.location.reload(true); }, 600);
        } catch {
            setBusy(false);
            window.location.reload(true);
        }
    };

    const deleteAccount = async () => {
        const confirm = window.prompt(t('profile.delete_prompt'));
        if (confirm !== 'DELETE') return;
        setBusy(true);
        try {
            await api.delete('/auth/me');
            try { localStorage.removeItem('session_token'); } catch { /* ignore */ }
            toast.success(t('profile.deleted'));
            setTimeout(() => { window.location.replace('/login'); }, 800);
        } catch (error) {
            toast.error(error?.response?.data?.detail?.code === 'account_ai_in_progress'
                ? (locale === 'da'
                    ? 'En AI-opgave afregnes stadig. Se AI-historikken og prøv sletning igen, når den er afsluttet. Kontakt support, hvis den bliver ved med at afvente.'
                    : 'An AI request is still being settled. Check AI history and retry deletion after it finishes. Contact support if it stays pending.')
                : t('profile.delete_fail'));
            setBusy(false);
        }
    };

    if (!user) {
        return (
            <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto">
                <h1 className="font-serif text-3xl text-brand-ink mb-4">{t('profile.title')}</h1>
                <button onClick={() => navigate('/login')} className="bg-brand-green text-white px-6 py-3 rounded-full">{t('login.cta')}</button>
            </div>
        );
    }

    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <div className="avatar-alive shrink-0">
                        {user.picture ? (
                            <img src={pictureUrl(user.picture)} alt="" className="w-16 h-16 rounded-full border-2 border-brand-bg object-cover" />
                        ) : (
                            <div className="w-16 h-16 rounded-full bg-brand-bg border-2 border-brand-bg flex items-center justify-center text-2xl font-serif text-brand-green">{user.name?.[0]?.toUpperCase()}</div>
                        )}
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="font-serif text-2xl text-brand-ink">{user.name}</h1>
                            {user.premium && (
                                <span data-testid="premium-badge" className="text-[10px] uppercase tracking-widest font-semibold bg-brand-ochre/20 text-brand-ochre px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                    <Crown size={10} /> Premium
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-brand-ink3">{user.email}</p>
                    </div>
                </div>
                <LanguageSwitcher variant="inline" />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6">
                <button onClick={() => setPhotoPicker(true)} disabled={profileBusy} className="bg-brand-card border border-brand-border rounded-2xl px-4 py-3 inline-flex items-center justify-center gap-2 text-sm text-brand-ink hover:border-brand-green/50 disabled:opacity-50"><Camera size={15} /> {locale === 'da' ? 'Vælg plantefoto' : 'Choose plant photo'}</button>
                <button onClick={shareProfile} disabled={profileBusy} className="bg-brand-green text-white rounded-2xl px-4 py-3 inline-flex items-center justify-center gap-2 text-sm disabled:opacity-50">{profileBusy ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />} {locale === 'da' ? 'Del profil' : 'Share profile'}</button>
            </div>

            {photoPicker && (
                <div className="fixed inset-0 z-[80] bg-brand-ink/45 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setPhotoPicker(false)}>
                    <div role="dialog" aria-modal="true" aria-labelledby="profile-photo-title" className="bg-brand-bg w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4"><h2 id="profile-photo-title" className="font-serif text-2xl text-brand-ink">{locale === 'da' ? 'Vælg profilbillede' : 'Choose profile photo'}</h2><button aria-label={locale === 'da' ? 'Luk billedvælger' : 'Close photo picker'} onClick={() => setPhotoPicker(false)} className="p-2"><X size={18} /></button></div>
                        {plants.length ? <div className="grid grid-cols-3 gap-3">{plants.map((plant) => <button key={plant.id} onClick={() => chooseProfilePhoto(plant.image_path)} disabled={profileBusy} className="text-left"><img src={fileUrl(plant.image_path)} alt={plant.name} className="w-full aspect-square object-cover rounded-2xl border border-brand-border" /><span className="block text-xs text-brand-ink mt-1 truncate">{plant.name}</span></button>)}</div> : <p className="text-sm text-brand-ink3 py-8 text-center">{locale === 'da' ? 'Tilføj først et billede til en plante i Min have.' : 'Add a photo to a plant in My Garden first.'}</p>}
                    </div>
                </div>
            )}

            {user.premium && (
                <button
                    data-testid="card-weekly-plan"
                    onClick={() => navigate('/weekly-plan')}
                    className="w-full mb-6 bg-gradient-to-br from-brand-green to-brand-greenDark text-white p-5 rounded-3xl flex items-center justify-between hover:opacity-95 transition-opacity"
                >
                    <div className="text-left">
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-bg/70 inline-flex items-center gap-1"><Sparkles size={11} /> AI</div>
                        <div className="font-serif text-lg mt-1">{t('plan.title')}</div>
                        <div className="text-xs text-brand-bg/80 mt-0.5">{t('plan.sub')}</div>
                    </div>
                    <ChevronRight size={20} />
                </button>
            )}

            {!user.premium && (
                <button
                    onClick={() => navigate('/premium')}
                    className="w-full mb-6 bg-gradient-to-br from-brand-ochre to-brand-terracotta text-white p-5 rounded-3xl flex items-center justify-between hover:opacity-95"
                >
                    <div className="text-left">
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-white/80 inline-flex items-center gap-1"><Crown size={11} /> Premium</div>
                        <div className="font-serif text-lg mt-1">{t('premium.upsell_title')}</div>
                        <PremiumFromPrice />
                    </div>
                    <ChevronRight size={20} />
                </button>
            )}

            {ach && (
                <div className="grid grid-cols-3 gap-3 mb-8">
                    <Stat icon="🌱" label={t('profile.stat_plants')} value={ach.stats.plants} />
                    <Stat icon="✅" label={t('profile.stat_tasks')} value={ach.stats.tasks_done} />
                    <Stat icon="📔" label={t('profile.stat_notes')} value={ach.stats.journal} />
                </div>
            )}

            <InviteCard />

            <DisplayNameCard />

            <ThemeSwitcher />

            <section className="mb-8" data-testid="ai-provider-settings">
                <h2 className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3 inline-flex items-center gap-1">
                    <Sparkles size={11} /> {locale === 'da' ? 'AI-indstillinger' : 'AI settings'}
                </h2>
                <div className="bg-brand-card border border-brand-border rounded-2xl p-4 space-y-3">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-brand-green/15 text-brand-green flex items-center justify-center flex-shrink-0"><Cloud size={19} /></div>
                        <div className="min-w-0">
                            <div className="font-medium text-sm text-brand-ink">{locale === 'da' ? 'GrowDaily AI (anbefalet)' : 'GrowDaily AI (recommended)'}</div>
                            <p className="text-xs text-brand-ink3 mt-0.5">{user.premium
                                ? (locale === 'da' ? 'Premium bruger automatisk den administrerede Claude-model — ingen ekstra opsætning.' : 'Premium automatically uses the managed Claude model — no extra setup.')
                                : (locale === 'da' ? 'Sikker cloud-model med dine normale AI-kvoter.' : 'Secure cloud model with your normal AI quotas.')}</p>
                        </div>
                    </div>
                    <label className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer ${aiPreference === 'managed' ? 'border-brand-green bg-brand-green/5' : 'border-brand-border'}`}>
                        <input type="radio" name="ai-provider" checked={effectiveAiPreference === 'managed'} onChange={() => changeAiPreference('managed')} />
                        <span className="text-sm text-brand-ink">{locale === 'da' ? 'Brug GrowDaily AI' : 'Use GrowDaily AI'}</span>
                    </label>
                    <label className={`flex items-center gap-3 rounded-xl border p-3 ${user.premium ? 'opacity-55 cursor-not-allowed' : 'cursor-pointer'} ${aiPreference === 'local' ? 'border-brand-ochre bg-brand-ochre/5' : 'border-brand-border'}`}>
                        <input type="radio" name="ai-provider" checked={effectiveAiPreference === 'local'} onChange={() => changeAiPreference('local')} disabled={!!user.premium} />
                        <Cpu size={17} className="text-brand-ochre" />
                        <span className="text-sm text-brand-ink">{locale === 'da' ? 'Lokal AI på min PC/telefon' : 'Local AI on my PC/phone'}</span>
                        <span className="ml-auto text-[10px] text-brand-ink3">{user.premium ? (locale === 'da' ? 'Premium: cloud' : 'Premium: cloud') : (locale === 'da' ? 'Tilslut kræves' : 'Connection required')}</span>
                    </label>
                    <p className="text-[11px] text-brand-ink3 leading-relaxed">{user.premium
                        ? (locale === 'da' ? 'Din Premium-adgang skifter ikke til en ukendt lokal model. Det beskytter kvalitet, support og dine inkluderede AI-funktioner.' : 'Premium never silently switches to an unknown local model. This protects quality, support and included AI features.')
                        : (locale === 'da' ? 'Lokal AI sender ikke billeder til GrowDaily, når den er korrekt tilsluttet. Vi viser først lokale funktioner som aktive, når en sikker forbindelse er parret.' : 'Local AI does not send images to GrowDaily when correctly connected. Local features remain inactive until a secure connection is paired.')}</p>
                </div>
            </section>

            {storage && (
                <section className="mb-8" data-testid="storage-overview">
                    <h2 className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3 inline-flex items-center gap-1"><HardDrive size={11} /> {locale === 'da' ? 'Billedlager' : 'Image storage'}</h2>
                    <div className="bg-brand-card border border-brand-border rounded-2xl p-4">
                        <div className="grid grid-cols-3 gap-3 text-center">
                            <div><div className="font-serif text-xl text-brand-ink">{storage.image_count}</div><div className="text-[10px] text-brand-ink3">{locale === 'da' ? 'billeder' : 'images'}</div></div>
                            <div><div className="font-serif text-xl text-brand-ink">{formatBytes(storage.total_bytes)}</div><div className="text-[10px] text-brand-ink3">{locale === 'da' ? 'i skyen' : 'in cloud'}</div></div>
                            <div><div className="font-serif text-xl text-brand-ink">{formatBytes(storage.average_bytes)}</div><div className="text-[10px] text-brand-ink3">{locale === 'da' ? 'pr. fil' : 'per file'}</div></div>
                        </div>
                        <p className="text-xs text-brand-ink2 leading-relaxed mt-4">
                            {locale === 'da'
                                ? `Billeder komprimeres til WebP og gemmes i skyen, så de synkroniserer og kan deles. Allerede viste billeder caches privat på denne enhed i ${storage.device_cache_days} dage. Med din nuværende gennemsnitsstørrelse svarer 1 GB omtrent til ${Number(storage.estimated_images_per_gib || 0).toLocaleString('da-DK')} billeder.`
                                : `Images are compressed to WebP and stored in the cloud for sync and sharing. Viewed images are privately cached on this device for ${storage.device_cache_days} days. At your current average size, 1 GB holds roughly ${Number(storage.estimated_images_per_gib || 0).toLocaleString('en')} images.`}
                        </p>
                        <p className="text-[10px] text-brand-ink3 mt-2">{locale === 'da' ? 'Antallet er et estimat baseret på dine nuværende filer og er ikke en lagerkvote.' : 'The figure is an estimate based on your current files, not a storage quota.'}</p>
                    </div>
                </section>
            )}

            <SubscriptionCard />

            <PushSettingsCard />

            <InstallPWAButton variant="card" />

            <section className="mb-8">
                <h2 className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3 inline-flex items-center gap-1">
                    <RefreshCw size={11} /> {t('profile.app_section')}
                </h2>
                <button
                    data-testid="btn-clear-cache"
                    onClick={clearCacheAndReload}
                    disabled={busy}
                    className="w-full flex items-center gap-4 bg-brand-card border border-brand-border rounded-2xl p-4 text-left hover:border-brand-green/50 transition-all disabled:opacity-50"
                >
                    <div className="w-11 h-11 rounded-2xl bg-brand-green/15 text-brand-green flex items-center justify-center flex-shrink-0">
                        <RefreshCw size={20} strokeWidth={1.7} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-serif text-base text-brand-ink">{t('profile.clear_cache')}</div>
                        <div className="text-xs text-brand-ink3 mt-0.5">{t('profile.clear_cache_sub')}</div>
                    </div>
                    <ChevronRight size={16} className="text-brand-ink3 flex-shrink-0" />
                </button>
            </section>


            <section className="mb-8">
                <h2 className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3">{t('phone.eyebrow')}</h2>
                <button
                    data-testid="link-phone-theme"
                    onClick={() => navigate('/phone-theme')}
                    className="w-full flex items-center gap-4 bg-brand-card border border-brand-border rounded-2xl p-4 text-left hover:-translate-y-0.5 hover:border-brand-green/50 transition-all"
                >
                    <div className="w-11 h-11 rounded-2xl bg-brand-green/15 text-brand-green flex items-center justify-center flex-shrink-0">
                        <Smartphone size={20} strokeWidth={1.7} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-serif text-base text-brand-ink">{t('profile.phone_theme')}</div>
                        <div className="text-xs text-brand-ink3 mt-0.5">{t('profile.phone_theme_sub')}</div>
                    </div>
                    <ChevronRight size={16} className="text-brand-ink3 flex-shrink-0" />
                </button>
            </section>

            <section className="mb-8">
                <h2 className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3">{t('profile.achievements')}</h2>
                <button
                    data-testid="link-achievements"
                    onClick={() => navigate('/achievements')}
                    className="w-full flex items-center gap-4 bg-brand-card border border-brand-border rounded-2xl p-4 text-left hover:-translate-y-0.5 hover:border-brand-green/50 transition-all"
                >
                    <div className="w-11 h-11 rounded-2xl bg-brand-ochre/20 text-brand-ochre flex items-center justify-center flex-shrink-0">
                        <Award size={20} strokeWidth={1.7} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-serif text-base text-brand-ink">{t('profile.tasks_ach')}</div>
                        <div className="text-xs text-brand-ink3 mt-0.5">
                            {ach ? `${ach.unlocked_count}/${ach.total_count} ${t('profile.badges_progress')}` : t('profile.badges_sub')}
                        </div>
                    </div>
                    <ChevronRight size={16} className="text-brand-ink3 flex-shrink-0" />
                </button>
            </section>

            <button
                data-testid="btn-logout"
                onClick={logout}
                className="w-full inline-flex items-center justify-center gap-2 bg-brand-card border border-brand-border text-brand-ink py-3 rounded-full hover:bg-brand-bg2"
            >
                <LogOut size={15} strokeWidth={1.8} /> {t('profile.logout')}
            </button>

            <Link
                data-testid="link-help"
                to="/help"
                className="mt-3 w-full inline-flex items-center justify-between bg-brand-card border border-brand-border text-brand-ink py-3 px-4 rounded-2xl hover:bg-brand-bg2"
            >
                <span className="inline-flex items-center gap-2 text-sm"><HelpCircle size={15} strokeWidth={1.8} /> {t('profile.help_guide')}</span>
                <ChevronRight size={16} className="text-brand-ink3" />
            </Link>

            <div className="mt-10">
                <CommunityGuidelinesCard />
            </div>

            <div className="mt-10">
                <SocialLinks />
            </div>

            {isFounder && (
                <section className="mb-8" data-testid="founder-tools">
                    <h2 className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3 inline-flex items-center gap-1">
                        <CreditCard size={11} /> Founder tools
                    </h2>
                    <div className="bg-brand-card border border-brand-border rounded-2xl p-4 space-y-3">
                        <div>
                            <p className="text-xs text-brand-ink2 leading-relaxed mb-2">
                                Marketplace waitlist: <strong>{waitlist?.total ?? '—'}</strong> subscribers
                                {waitlist?.push_optin ? ` · ${waitlist.push_optin} want push` : ''}. One click notifies everyone the marketplace is open.
                            </p>
                            <button
                                data-testid="btn-broadcast-waitlist"
                                onClick={broadcastWaitlist}
                                disabled={broadcasting || !waitlist?.total}
                                className="w-full inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full text-sm font-medium disabled:opacity-50 transition-colors"
                            >
                                {broadcasting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                                {broadcasting ? 'Sending…' : 'Announce “Marketplace is open!”'}
                            </button>
                        </div>
                    </div>
                </section>
            )}

            <section className="mt-10">
                <h2 className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3 inline-flex items-center gap-1">
                    <Shield size={11} /> {t('profile.gdpr')}
                </h2>
                <div className="space-y-2">
                    <button
                        data-testid="btn-export-data"
                        onClick={exportData}
                        disabled={busy}
                        className="w-full inline-flex items-center justify-between bg-brand-card border border-brand-border text-brand-ink py-3 px-4 rounded-2xl hover:bg-brand-bg2 disabled:opacity-50"
                    >
                        <span className="inline-flex items-center gap-2 text-sm"><Download size={15} strokeWidth={1.8} /> {t('profile.export')}</span>
                        <ChevronRight size={16} className="text-brand-ink3" />
                    </button>
                    <Link
                        data-testid="link-privacy"
                        to="/privacy"
                        className="w-full inline-flex items-center justify-between bg-brand-card border border-brand-border text-brand-ink py-3 px-4 rounded-2xl hover:bg-brand-bg2"
                    >
                        <span className="inline-flex items-center gap-2 text-sm"><Shield size={15} strokeWidth={1.8} /> {t('profile.privacy_policy')}</span>
                        <ChevronRight size={16} className="text-brand-ink3" />
                    </Link>
                    {user?.is_admin && (
                        <Link
                            data-testid="link-admin-stats"
                            to="/admin/stats"
                            className="w-full inline-flex items-center justify-between bg-brand-card border border-brand-border text-brand-ink py-3 px-4 rounded-2xl hover:bg-brand-bg2"
                        >
                            <span className="inline-flex items-center gap-2 text-sm"><BarChart3 size={15} strokeWidth={1.8} /> {t('profile.user_stats')}</span>
                            <ChevronRight size={16} className="text-brand-ink3" />
                        </Link>
                    )}
                    {user?.is_admin && (
                        <Link
                            data-testid="link-admin-links"
                            to="/admin/links"
                            className="w-full inline-flex items-center justify-between bg-brand-card border border-brand-border text-brand-ink py-3 px-4 rounded-2xl hover:bg-brand-bg2"
                        >
                            <span className="inline-flex items-center gap-2 text-sm"><LinkIcon size={15} strokeWidth={1.8} /> Support links</span>
                            <ChevronRight size={16} className="text-brand-ink3" />
                        </Link>
                    )}
                    {user?.is_admin && (
                        <Link
                            data-testid="link-admin-studio"
                            to="/admin/studio"
                            className="w-full inline-flex items-center justify-between bg-brand-card border border-brand-border text-brand-ink py-3 px-4 rounded-2xl hover:bg-brand-bg2"
                        >
                            <span className="inline-flex items-center gap-2 text-sm"><Wand2 size={15} strokeWidth={1.8} /> App Studio</span>
                            <ChevronRight size={16} className="text-brand-ink3" />
                        </Link>
                    )}
                    {user?.is_admin && (
                        <Link
                            data-testid="link-admin-moderation"
                            to="/admin/moderation"
                            className="w-full inline-flex items-center justify-between bg-brand-card border border-brand-border text-brand-ink py-3 px-4 rounded-2xl hover:bg-brand-bg2"
                        >
                            <span className="inline-flex items-center gap-2 text-sm"><ShieldAlert size={15} strokeWidth={1.8} /> {t('profile.moderation')}</span>
                            <ChevronRight size={16} className="text-brand-ink3" />
                        </Link>
                    )}
                    <button
                        data-testid="btn-delete-account"
                        onClick={deleteAccount}
                        disabled={busy}
                        className="w-full inline-flex items-center justify-between bg-brand-terracotta/10 border border-brand-terracotta/30 text-brand-terracotta py-3 px-4 rounded-2xl hover:bg-brand-terracotta/20 disabled:opacity-50"
                    >
                        <span className="inline-flex items-center gap-2 text-sm"><Trash2 size={15} strokeWidth={1.8} /> {t('profile.delete')}</span>
                        <ChevronRight size={16} />
                    </button>
                </div>
                <p className="text-[10px] text-brand-ink3 mt-3 leading-relaxed">
                    {t('profile.gdpr_note')}
                </p>
            </section>

            <footer className="mt-10 text-center" data-testid="uniqapps-credit">
                <OperatorNotice />
                <p className="text-[10px] text-brand-ink3">
                    GrowDaily v1.0 · {t('profile.app_by')}{' '}
                    <a href="https://uniqapps.space" target="_blank" rel="noreferrer" className="text-brand-green hover:underline">
                        Uniqapps.space
                    </a>
                </p>
            </footer>
        </div>
    );
}

function formatBytes(value) {
    const bytes = Number(value || 0);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
    return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

function Stat({ icon, label, value }) {
    return (
        <div className="bg-brand-card border border-brand-border rounded-2xl p-4 text-center">
            <div className="text-2xl mb-1">{icon}</div>
            <div className="font-serif text-2xl text-brand-ink leading-none">{value}</div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mt-1">{label}</div>
        </div>
    );
}
